import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { getEffectiveLockedFields } from '@/services/data/lockedFieldsClient';

/**
 * BI-5-03 UI delivery for server-resolved franchise lock context.
 *
 * Wraps the `get_effective_locked_fields()` SECURITY DEFINER RPC (via the
 * non-React {@link getEffectiveLockedFields} client) in a TanStack Query so
 * Media Studio / PostBody affordances can render lock states.
 *
 * PHASE-PINNED SEMANTIC (BI-5-01, binding across the whole phase):
 *   - `lockedFields`          = DISPLAY-ONLY union of own + ancestor locks.
 *   - `inheritedLockedFields` = THE enforcement set (ancestor locks ONLY).
 *     Every lock affordance in the UI keys off THIS, never the display union —
 *     a corporate account editing its OWN locked values must never see a lock.
 *
 * Defaults to empty locks while loading so consumer UI never flashes a lock
 * state, and fails open (the wrapped client already logs + returns empty on
 * RPC error) — the DB trigger + BI-5-02 routes remain the authoritative stops.
 */
export interface UseEffectiveLockedFieldsResult {
  lockedFields: string[];
  inheritedLockedFields: string[];
  lockedValues: Record<string, string>;
  isLoading: boolean;
}

const EMPTY: Omit<UseEffectiveLockedFieldsResult, 'isLoading'> = {
  lockedFields: [],
  inheritedLockedFields: [],
  lockedValues: {},
};

export function useEffectiveLockedFields(): UseEffectiveLockedFieldsResult {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const query = useQuery({
    queryKey: ['effective-locked-fields', userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: () => getEffectiveLockedFields(supabase),
  });

  return useMemo(
    () => ({
      lockedFields: query.data?.lockedFields ?? EMPTY.lockedFields,
      inheritedLockedFields: query.data?.inheritedLockedFields ?? EMPTY.inheritedLockedFields,
      lockedValues: query.data?.lockedValues ?? EMPTY.lockedValues,
      isLoading: query.isLoading,
    }),
    [query.data, query.isLoading],
  );
}
